import React from "react";
import Box from "@mui/material/Box";
import Modal from "@mui/material/Modal";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import GavelIcon from "@mui/icons-material/Gavel";
import { API_get_compliance_audit } from "../../../../constants";
import { getRightworkAPI } from "../../requests/utilities/requests";
import { formatDateReadableWithDayOfWeek } from "../../requests/utilities/helpers";
import { getDailyComplianceAudit } from "./dailyComplianceAudit";

export default function DailyLaborComplianceBadge({ storeId, date, refreshing }) {
  const [open, setOpen] = React.useState(false);
  const [audit, setAudit] = React.useState(null);
  const [error, setError] = React.useState(false);
  const [retry, setRetry] = React.useState(0);

  React.useEffect(() => {
    let active = true;
    setAudit(null);
    setError(false);
    if (!refreshing && storeId && date) {
      getRightworkAPI(API_get_compliance_audit, { store_id: storeId, date })
        .then((response) => {
          const payload = response.data?.redux?.payload;
          if (response.status !== 200 || !Array.isArray(payload?.groups)) {
            throw new Error("Compliance audit unavailable");
          }
          if (active) setAudit(getDailyComplianceAudit(payload, date));
        })
        .catch(() => {
          if (active) setError(true);
        });
    }
    return () => { active = false; };
  }, [storeId, date, refreshing, retry]);

  const loading = !audit && !error;
  const hasViolations = audit?.total_violations > 0;

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-haspopup="dialog"
        className={`inline-flex items-center gap-1.5 rounded border px-2 py-1 text-xs font-medium focus-visible:outline focus-visible:outline-2 focus-visible:outline-violet-600 ${
          hasViolations
            ? "border-red-200 bg-red-50 text-red-700 hover:bg-red-100"
            : "border-slate-300 bg-slate-50 text-slate-700 hover:bg-slate-100"
        }`}
      >
        <GavelIcon sx={{ fontSize: 15 }} />
        Labor law violations: {loading ? "Checking…" : error ? "Unavailable" : audit.total_violations}
      </button>
      <Modal open={open} onClose={() => setOpen(false)} aria-labelledby="daily-compliance-title">
        <Box
          role="dialog"
          aria-modal="true"
          aria-labelledby="daily-compliance-title"
          sx={{
            position: "absolute", top: "50%", left: "50%",
            transform: "translate(-50%, -50%)", width: 760,
            maxWidth: "calc(100vw - 32px)", maxHeight: "85vh", overflowY: "auto",
            bgcolor: "background.paper", border: "2px solid #E2E8F0",
            borderRadius: "15px", boxShadow: 24, p: { xs: 2, sm: 3 },
          }}
        >
          <div className="flex items-start justify-between gap-4">
            <div>
              <h2 id="daily-compliance-title" className="text-xl font-semibold text-slate-900">Labor law violations</h2>
              <p className="mt-1 text-sm text-slate-500">{formatDateReadableWithDayOfWeek(date)}</p>
            </div>
            <IconButton aria-label="Close labor law violations" onClick={() => setOpen(false)} size="small">
              <CloseIcon />
            </IconButton>
          </div>
          {loading && <p role="status" className="py-8 text-sm text-slate-500">Checking time entries…</p>}
          {error && (
            <div role="alert" className="mt-5 rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800">
              Unable to load labor law violations.
              <button type="button" onClick={() => setRetry((value) => value + 1)} className="ml-2 font-semibold underline">Try again</button>
            </div>
          )}
          {audit && (
            <div className="mt-5">
              <p className={`text-sm font-medium ${hasViolations ? "text-red-700" : "text-slate-700"}`}>
                {hasViolations
                  ? `${audit.total_violations} violation${audit.total_violations === 1 ? "" : "s"} found`
                  : "No labor law violations found for this day."}
              </p>
              <p className="mt-1 text-xs text-slate-500">
                Based on completed time entries and supported rules. Weekly findings appear on the date assigned by the compliance audit.
              </p>
              <div className="mt-4 space-y-4">
                {audit.groups.map((group, index) => (
                  <section key={`${group.regulation_type}-${index}`} className="overflow-hidden rounded-lg border border-slate-200">
                    <div className="flex items-center justify-between gap-3 border-b border-slate-200 bg-slate-50 px-4 py-3">
                      <h3 className="text-sm font-semibold text-slate-900">{group.regulation_type_name}</h3>
                      <span className="inline-flex h-6 min-w-[24px] shrink-0 self-center items-center justify-center rounded-full bg-red-100 px-2 text-xs font-medium leading-none text-red-700">{group.violation_count}</span>
                    </div>
                    <ul className="divide-y divide-slate-100">
                      {group.violations.map((violation, row) => (
                        <li key={row} className="px-4 py-3">
                          <p className="text-sm font-medium text-slate-900">{violation.employee_name}</p>
                          <p className="mt-1 text-sm text-slate-600">{violation.violation_detail}</p>
                        </li>
                      ))}
                    </ul>
                  </section>
                ))}
              </div>
              {audit.skipped_regulation_types?.length > 0 && (
                <p className="mt-4 rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800">
                  Not audited from time entries: {audit.skipped_regulation_types.map((item) => item.regulation_type_name).join(", ")}.
                </p>
              )}
            </div>
          )}
        </Box>
      </Modal>
    </>
  );
}
